<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import { ImageIcon } from 'lucide-vue-next'
  import { useReportThemesStore } from '@stores/reportThemes/reportThemes'
  import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
  import { Label } from '@/components/ui/label'
  import ReportThemeTextStyleRow from './ReportThemeTextStyleRow.vue'
  import type { TextStyle, SkinKey } from '@/types'

  type SkinKeyWithText = 'cover' | 'section' | 'content'

  // Fields to exclude from text style editing (chart placeholders, not text)
  const EXCLUDED_FIELDS = new Set(['chart', 'placeholder'])

  // Preferred display order for field names (suffix after the skin prefix)
  const FIELD_ORDER = ['title', 'subtitle', 'text_default', 'text_small', 'body', 'slide_number']

  const props = defineProps<{
    skinSection: { key: SkinKey; label: string; hasTexts: boolean }
    detectedFonts: string[]
  }>()

  const store = useReportThemesStore()
  const { currentTheme, localEdits } = storeToRefs(store)

  function backgroundPreview(skin: SkinKey): string | null {
    return (currentTheme.value?.backgrounds as Record<string, { preview_base64?: string | null }>)?.[skin]?.preview_base64 ?? null
  }

  function textStylesForSkin(skin: SkinKeyWithText): Record<string, TextStyle> {
    const styles = localEdits.value?.text_styles as Record<string, TextStyle> | undefined ?? {}
    const prefix = skin + '_'

    const entries = Object.entries(styles).filter(([key]) => {
      if (!key.startsWith(prefix)) return false
      const field = key.slice(prefix.length)
      return !EXCLUDED_FIELDS.has(field)
    })

    entries.sort(([a], [b]) => {
      const fa = a.slice(prefix.length)
      const fb = b.slice(prefix.length)
      const ia = FIELD_ORDER.indexOf(fa)
      const ib = FIELD_ORDER.indexOf(fb)
      if (ia === -1 && ib === -1) return fa.localeCompare(fb)
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    })

    return Object.fromEntries(entries)
  }

  function fieldLabel(key: string): string {
    return key.split('_').slice(1).join(' ')
  }

  function setTextStyle(key: string, style: TextStyle) {
    if (!localEdits.value) return
    if (!localEdits.value.text_styles) localEdits.value.text_styles = {}
    ;(localEdits.value.text_styles as Record<string, TextStyle>)[key] = style
  }
</script>

<template>
  <AccordionItem :value="props.skinSection.key">
    <AccordionTrigger class="text-base font-semibold">{{ props.skinSection.label }}</AccordionTrigger>
    <AccordionContent>
      <div class="space-y-5 pt-1">
        <!-- Background — compact thumbnail -->
        <div>
          <Label>Background</Label>
          <div class="mt-1.5 h-24 w-auto inline-flex aspect-video rounded-md border overflow-hidden bg-muted items-center justify-center">
            <img
              v-if="backgroundPreview(props.skinSection.key)"
              :src="`data:image/png;base64,${backgroundPreview(props.skinSection.key)}`"
              class="h-full w-auto object-cover"
              :alt="props.skinSection.label" />
            <ImageIcon
              v-else
              class="w-6 h-6 text-muted-foreground/20" />
          </div>
        </div>

        <!-- Texts (only for slides with text fields) -->
        <div v-if="props.skinSection.hasTexts">
          <Label>Texts</Label>
          <div
            v-if="Object.keys(textStylesForSkin(props.skinSection.key as SkinKeyWithText)).length > 0"
            class="mt-2">
            <!-- Column headers -->
            <div class="flex items-center gap-4 pb-1 mb-1">
              <span class="w-24 shrink-0" />
              <span class="w-10 shrink-0 text-xs text-muted-foreground text-center">Color</span>
              <span class="w-44 shrink-0 text-xs text-muted-foreground">Font</span>
              <span class="w-14 shrink-0 text-xs text-muted-foreground">Size</span>
              <span class="w-10 shrink-0 text-xs text-muted-foreground">Bold</span>
            </div>
            <ReportThemeTextStyleRow
              v-for="(style, key) in textStylesForSkin(props.skinSection.key as SkinKeyWithText)"
              :key="key"
              :label="fieldLabel(key)"
              :style="style"
              :detected-fonts="props.detectedFonts"
              @update="(v) => setTextStyle(key, v)" />
          </div>
          <p
            v-else
            class="text-xs text-muted-foreground italic mt-1.5">
            Upload a PPTX to configure text styles.
          </p>
        </div>
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
